import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AI_ALERT_SUMMARY_TABS } from '@utils/aiAlerts/mapping';
import {
  aiAlertsCIRRFeatureSelector,
  aiAlertsPCPFeatureSelector,
  bleEnabledSelector,
  featureSelector,
  appAIAlertFeatureFlagSelector,
} from '@src/store/common/selectors';
import {
  selectedTabSelector,
  tagsSelector,
  loadingSelector,
} from '@store/aiAlerts/summary/selectors';
import {
  setSelectedTab,
  resetMetricSelection,
  resetSeveritySelection,
} from '@store/aiAlerts/summary/actions';
import { Retry } from '@components/common';
import FeatureCheck from '@components/common/FeatureCheck';
import SummaryTable from './summaryTable';
import TagsContainer from './tagsContainer';
import { Wrapper, TabContentWrapper } from './styled';
import StyledTabs from '@components/common/Tabs';
import ErrorBoundary from '@components/common/Retry/ErrorBoundary';

const Content = () => {
  const dispatch = useDispatch();
  const selectedTab = useSelector(selectedTabSelector);
  const { alertsBySeverity, alertsByMetric, alertsByActiveStatus }: any = useSelector(tagsSelector);
  const isLoading = useSelector(loadingSelector);
  const isAlertPartnerAccount = useSelector(featureSelector('is_alert_forwarding_partner'));
  const isAdAlertsEnabled = useSelector(featureSelector('ad_ai_alerts'));
  const isContentAlertsEnabled = useSelector(featureSelector('ai_alerts')) || isAlertPartnerAccount;
  const isAppAlertEnabled = useSelector(appAIAlertFeatureFlagSelector); // this is false
  const isPcpEnabled = useSelector(aiAlertsPCPFeatureSelector);
  const isBusinessMetricsEnabled = useSelector(bleEnabledSelector);
  const isAllTabsValid = isAdAlertsEnabled || isContentAlertsEnabled || isAppAlertEnabled;
  const alertFeatureFlags = {
    isAdAlertsEnabled,
    isContentAlertsEnabled,
    isAllTabsValid,
    isAlertPartnerAccount,
    isAppAlertEnabled,
    isPcpEnabled,
    isBusinessMetricsEnabled,
  };

  // clear tag selections on tab change
  useEffect(() => {
    dispatch(resetSeveritySelection());
    dispatch(resetMetricSelection());
  }, [selectedTab]);

  const onChangeTab = (key) => {
    dispatch(setSelectedTab(key));
  };

  const getContent = (tab) => (
    <ErrorBoundary>
      <TabContentWrapper>
        <TagsContainer
          type={tab.type}
          alertsBySeverity={alertsBySeverity[tab.type]}
          alertsByMetric={alertsByMetric}
          alertFeatureFlags={alertFeatureFlags}
          alertsByActiveStatus={alertsByActiveStatus}
        />
        <SummaryTable type={tab.type} alertFeatureFlags={alertFeatureFlags} />
      </TabContentWrapper>
    </ErrorBoundary>
  );

  const getContentByTabType = (tab) => {
    switch (true) {
      case tab.type === 'all' && isAllTabsValid:
        return getContent(tab);
      case tab.type === 'EI' && isContentAlertsEnabled:
        return getContent(tab);
      case tab.type === 'AI' && isAdAlertsEnabled:
        return getContent(tab);
      case tab.type === 'APP' && isAppAlertEnabled:
        return getContent(tab);
      default:
        return (
          <TabContentWrapper>
            <FeatureCheck featureName="" />
          </TabContentWrapper>
        );
    }
  };

  if (isLoading) {
    return <Retry isLoading={isLoading} />;
  }

  return (
    <Wrapper>
      <StyledTabs
        onChange={onChangeTab}
        defaultActiveKey={selectedTab}
        items={AI_ALERT_SUMMARY_TABS.map((tab) => ({
          label: tab.title,
          key: tab.type,
          children: getContentByTabType(tab),
        }))}
      />
    </Wrapper>
  );
};

export default Content;
